import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type Journal = components['schemas']['JournalResource']
export type JournalBody = components['schemas']['JournalStoreRequest']
export type JournalUpdate = components['schemas']['JournalUpdateRequest']

export function useJournals(companyId: string | null, activeOnly = false) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'journals', activeOnly] as const,
    enabled: Boolean(companyId),
    queryFn: async (): Promise<Journal[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/journals', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load journals')
      return response.data?.data ?? []
    },
    select: (journals) => (activeOnly ? journals.filter((journal) => journal.is_active) : journals),
  })
}

export function useCreateJournal(companyId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: JournalBody): Promise<Journal> => {
      const response = await api.POST('/accounting/journals', {
        headers: { 'X-Company-Id': companyId ?? '' },
        body,
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create journal')
      if (!response.data?.data) throw new Error('Malformed journal response')
      return response.data.data
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] }),
  })
}

export function useUpdateJournal(companyId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: JournalUpdate }): Promise<Journal> => {
      const response = await api.PATCH('/accounting/journals/{journal}', {
        params: { path: { journal: id } },
        headers: { 'X-Company-Id': companyId ?? '' },
        body,
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to update journal')
      if (!response.data?.data) throw new Error('Malformed journal response')
      return response.data.data
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] }),
  })
}
